import { NextSeo, WebPageJsonLd } from "next-seo";
import { AggregateGrid } from "~/components/AggregateDisplay";
import { Navbar } from "~/components/Navbar/component";
import { PageHeader } from "~/components/PageHeader";
import { PageWrapper, SidebarLayout } from "~/components/SidebarLayout";
import { ASSET_CLASS_IDS } from "~/consts";
import { PageTrack } from "~/modules/analytics/components/PageTrack";
import { addBaseUrl } from "~/modules/app/seo/addBaseUrl";
import { BreadcrumbSEO } from "~/modules/app/seo/BreadcrumbSEO";
import { getHomePageSeo } from "~/modules/app/seo/getHomePageSeo";
import { getTickerMarqueeData } from "~/modules/asset_classes/api/getTickerMarqueeData";
import { queryAssets } from "~/modules/assets/api/router";
import { DEFAULT_ASSET_LIST_QUERY } from "~/modules/assets/consts";
import { AssetTickerMarquee } from "~/modules/assets/components/AssetTickerMarquee";
import type { MarqueeTickerGroup } from "~/modules/assets/components/AssetTickerMarquee";
import { AssetColumns } from "~/modules/assets/types";
import { queryAssetClasses } from "~/modules/asset_classes/api/router";
import { AssetClassColumns } from "~/modules/asset_classes/types";
import {
    LeagueTableGroupTypes,
    LeagueTableTabsByDistributionType,
} from "~/modules/league_tables/types";
import { LatestAssetsCard } from "~/modules/main/pages/home/LatestAssetsCard";
import { getLatestAssetRows } from "~/modules/main/pages/home/LatestAssetsCard/getLatestAssetRows";
import type { LatestAssetRow } from "~/modules/main/pages/home/LatestAssetsCard/types";
import { TotalRwaValueChart } from "~/modules/main/pages/home/component";
import { HIGH_YIELD_ASSET_CLASS_IDS } from "~/modules/main/pages/home/consts";
import { HomeHighYieldChart } from "~/modules/main/pages/home/HomeHighYieldChart";
import { HomePageLeagueTable } from "~/modules/main/pages/home/HomePageLeagueTable";
import { LeagueTablePieChart } from "~/modules/league_tables/components/LeagueTablePieChart";
import { HomeNetFlowsCard } from "~/modules/main/pages/home/HomeNetFlowsCard";
import { HomeTreasuryYieldChart } from "~/modules/main/pages/home/HomeTreasuryYieldChart";
import {
    GlobalAssetTableData,
    GlobalAssetTableLayout,
} from "~/modules/main/pages/home/GlobalAssetTable/component";
import { queryAssetScreenerStats } from "~/modules/asset_screener/api/queryAssetScreenerStats";
import { queryAssetScreenerFilterMetadata } from "~/modules/asset_screener/api/queryAssetScreenerFilterMetadata";
import { ASSET_SCREENER_DEFAULT_QUERY } from "~/modules/asset_screener/consts";
import {
    getOverviewSummaryData,
    type OverviewSummaryData,
} from "~/modules/main/pages/home/getOverviewSummaryData";
import { FilterOperators, SortDirections } from "~/modules/filters/types";
import {
    TokenTimeseriesColumns,
    TokenTimeseriesGroupBy,
} from "~/modules/token_timeseries/types";
import { UserFetcher } from "~/modules/user/components/UserFetcher";
import type { NetFlowsItemLabels } from "~/modules/visualization/components/NetFlowsChart/component";
import { VisualizationGrid } from "~/modules/visualization/components/VisualizationGrid";
import {
    NetFlowsData,
    queryNetFlows,
} from "~/modules/fund_flows/api/queryNetFlows";
import { Routes } from "~/routes";
import { NEXTJS_REVALIDATE_TIMEOUT } from "~/server/consts";
import { queryLeagueTablesByDistributionType } from "~/modules/league_tables/api/queryLeagueTablesByDistributionType";

// // // //

const EXCLUDE_STABLECOINS_FILTERS = [
    {
        field: TokenTimeseriesColumns.assetClassID,
        operator: FilterOperators.NotEquals,
        value: ASSET_CLASS_IDS.Stablecoin,
    },
];
const NET_FLOW_ASSET_MANAGER_LABEL_MAX_LENGTH = 30;
const MIN_CREDIT_YIELD_CIRCULATING_VALUE = 10_000_000;
// Excluded after manual QA for implausible, zero-heavy, or stale APY data.
const CREDIT_YIELD_EXCLUDED_ASSET_TICKERS = [
    "ACRED",
    "BR0OVSCTF004",
    "BR0OVSCTF012",
    "BRVERTDBS0I1",
    "GSTR",
    "mGLO",
    "mGLOBAL",
    "pEAK",
    "RLP",
    "STAC",
    "USD3",
    "xSOLY",
];

function truncateNetFlowAssetManagerLabel(name: string): string {
    if (name.length <= NET_FLOW_ASSET_MANAGER_LABEL_MAX_LENGTH) {
        return name;
    }

    return `${name
        .slice(0, NET_FLOW_ASSET_MANAGER_LABEL_MAX_LENGTH - 1)
        .trimEnd()}…`;
}

interface HomePageProps extends OverviewSummaryData {
    globalAssetTable: GlobalAssetTableData;
    latestAssets: { rows: LatestAssetRow[]; total: number };
    tickerGroups: MarqueeTickerGroup[];
    leagueTableTabs: LeagueTableTabsByDistributionType;
    netFlowsByAssetClass: NetFlowsData;
    netFlowsByAsset: NetFlowsData;
    netFlowsByManager: NetFlowsData;
    netFlowAssetLabels: NetFlowsItemLabels;
    creditYieldAssetIDs: number[];
    highYieldAssetClassColors: Record<number, string | null>;
}

export async function getStaticProps() {
    const [
        overviewSummary,
        latestAssets,
        tickerGroups,
        leagueTableTabs,
        netFlowsByAssetClass,
        netFlowsByAsset,
        netFlowsByManager,
        creditYieldAssets,
        highYieldAssetClasses,
        assetTableInitialState,
        assetTableFilterMetadata,
        assetTableStats,
    ] = await Promise.all([
        getOverviewSummaryData(),
        // NOTE - the LatestAssetsCard uses the New Asset Monitor's filter so the
        // card and the monitor it links to always show the same assets
        getLatestAssetRows(),
        getTickerMarqueeData(),
        queryLeagueTablesByDistributionType({
            name: "home-page",
            filter: {
                field: TokenTimeseriesColumns.assetClassID,
                operator: FilterOperators.NotEquals,
                value: ASSET_CLASS_IDS.Stablecoin,
            },
            groups: [
                LeagueTableGroupTypes.networks,
                LeagueTableGroupTypes.managers,
                LeagueTableGroupTypes.platforms,
                LeagueTableGroupTypes.assetClasses,
            ],
        }),
        queryNetFlows({
            name: "home-by-asset-class-exclude-stablecoins",
            groupBy: TokenTimeseriesGroupBy.assetClass,
            filters: EXCLUDE_STABLECOINS_FILTERS,
        }),
        queryNetFlows({
            name: "home-by-asset-exclude-stablecoins",
            groupBy: TokenTimeseriesGroupBy.asset,
            filters: EXCLUDE_STABLECOINS_FILTERS,
        }),
        queryNetFlows({
            name: "home-by-manager-exclude-stablecoins",
            groupBy: TokenTimeseriesGroupBy.manager,
            filters: [
                ...EXCLUDE_STABLECOINS_FILTERS,
                {
                    field: TokenTimeseriesColumns.managerID,
                    operator: FilterOperators.GreaterThan,
                    value: 0,
                },
            ],
        }),
        queryAssets({
            query: {
                ...DEFAULT_ASSET_LIST_QUERY,
                name: "home-credit-yield-assets-over-10m",
                fields: ["id", "ticker"],
                filter: {
                    operator: FilterOperators.And,
                    filters: [
                        {
                            operator: FilterOperators.Or,
                            filters: HIGH_YIELD_ASSET_CLASS_IDS.map(
                                (assetClassID) => ({
                                    field: AssetColumns.assetClassID,
                                    operator: FilterOperators.Equals,
                                    value: assetClassID,
                                }),
                            ),
                        },
                        {
                            field: AssetColumns.circulatingAssetValueDollar,
                            operator: FilterOperators.GreaterThanOrEqual,
                            value: MIN_CREDIT_YIELD_CIRCULATING_VALUE,
                        },
                    ],
                },
                sort: {
                    field: AssetColumns.circulatingAssetValueDollar,
                    direction: SortDirections.desc,
                },
                pagination: {
                    page: 1,
                    perPage: 3500,
                },
            },
        }),
        queryAssetClasses({
            query: {
                name: "home-high-yield-asset-classes",
                filter: {
                    operator: FilterOperators.Or,
                    filters: HIGH_YIELD_ASSET_CLASS_IDS.map((assetClassID) => ({
                        field: AssetClassColumns.id,
                        operator: FilterOperators.Equals,
                        value: assetClassID,
                    })),
                },
                sort: {
                    field: AssetClassColumns.name,
                    direction: SortDirections.asc,
                },
                pagination: {
                    page: 1,
                    perPage: HIGH_YIELD_ASSET_CLASS_IDS.length,
                },
            },
        }),
        // Global asset table
        queryAssets({ query: ASSET_SCREENER_DEFAULT_QUERY }),
        queryAssetScreenerFilterMetadata(),
        queryAssetScreenerStats(),
    ]);

    const netFlowAssetIds = Array.from(
        new Set(
            Object.values(netFlowsByAsset)
                .flat()
                .map((item) => item.group.id),
        ),
    );
    const netFlowAssets = await (netFlowAssetIds.length > 0
        ? queryAssets({
              query: {
                  ...DEFAULT_ASSET_LIST_QUERY,
                  name: "home-net-flow-asset-labels",
                  filter: {
                      operator: FilterOperators.Or,
                      filters: netFlowAssetIds.map((assetId) => ({
                          field: AssetColumns.id,
                          operator: FilterOperators.Equals,
                          value: assetId,
                      })),
                  },
                  pagination: {
                      page: 1,
                      perPage: netFlowAssetIds.length,
                  },
              },
          })
        : { results: [] });
    const netFlowAssetLabels: NetFlowsItemLabels = Object.fromEntries(
        netFlowAssets.results.map((asset) => {
            const assetManager =
                asset.tokens.find((token) => token.asset_manager)
                    ?.asset_manager ??
                asset.manager ??
                asset.issuer;

            return [
                asset.id,
                {
                    primary: asset.ticker || asset.name,
                    secondary: assetManager?.name
                        ? truncateNetFlowAssetManagerLabel(assetManager.name)
                        : "—",
                },
            ];
        }),
    );

    return {
        props: {
            ...overviewSummary,
            latestAssets,
            tickerGroups,
            leagueTableTabs,
            netFlowsByAssetClass,
            netFlowsByAsset,
            netFlowsByManager,
            netFlowAssetLabels,
            creditYieldAssetIDs: creditYieldAssets.results
                .filter(
                    (asset) =>
                        asset.ticker === null ||
                        !CREDIT_YIELD_EXCLUDED_ASSET_TICKERS.includes(
                            asset.ticker,
                        ),
                )
                .map((asset) => asset.id),
            highYieldAssetClassColors: Object.fromEntries(
                highYieldAssetClasses.results.map((assetClass) => [
                    assetClass.id,
                    assetClass.color_hex,
                ]),
            ),
            globalAssetTable: {
                initialState: assetTableInitialState,
                filterMetadata: assetTableFilterMetadata,
                stats: assetTableStats,
            },
        } satisfies HomePageProps,
        revalidate: NEXTJS_REVALIDATE_TIMEOUT,
    };
}

export default function Home(props: HomePageProps) {
    const {
        aggregates,
        globalAssetTable,
        latestAssets,
        tickerGroups,
        leagueTableTabs,
        netFlowsByAssetClass,
        netFlowsByAsset,
        netFlowsByManager,
        netFlowAssetLabels,
        creditYieldAssetIDs,
        highYieldAssetClassColors,
    } = props;

    return (
        <PageTrack route={Routes.home}>
            <NextSeo
                canonical={addBaseUrl(Routes.home)}
                title="Analytics on Tokenized Real-World Assets"
            />
            <BreadcrumbSEO items={[]} />
            <WebPageJsonLd {...getHomePageSeo({ assetClasses: [] })} />
            <UserFetcher>
                {({ user }) => <Navbar route={Routes.home} user={user} />}
            </UserFetcher>
            <SidebarLayout pathname={Routes.home}>
                <AssetTickerMarquee groups={tickerGroups} />
                <PageWrapper>
                    <PageHeader
                        title="Global Market Overview"
                        subtitle={
                            <p>
                                Welcome to RWA.xyz. Discover every tokenized
                                real-world asset and the broader tokenization
                                ecosystem, from asset managers to tokenization
                                platforms to blockchains.{" "}
                                <span className="text-muted-foreground">
                                    Email team@rwa.xyz for questions or help.
                                </span>
                            </p>
                        }
                    />

                    <AggregateGrid aggregates={aggregates} />

                    <VisualizationGrid className="my-6 w-full">
                        <TotalRwaValueChart />

                        <HomePageLeagueTable
                            leagueTableTabs={leagueTableTabs}
                            analyticsTableName="homepage-league-table"
                            colSpans={{
                                sm: 12,
                                md: 12,
                                lg: 12,
                                xl: 6,
                                xxl: 6,
                            }}
                        />

                        <LeagueTablePieChart
                            title="Total RWA Value"
                            leagueTableTabs={leagueTableTabs}
                            defaultGroupBy={LeagueTableGroupTypes.platforms}
                            colSpans={{
                                sm: 12,
                                md: 12,
                                lg: 12,
                                xl: 6,
                                xxl: 6,
                            }}
                        />

                        <HomeTreasuryYieldChart />

                        <HomeHighYieldChart
                            assetClassColors={highYieldAssetClassColors}
                            creditYieldAssetIDs={creditYieldAssetIDs}
                        />
                        <HomeNetFlowsCard
                            colSpans={{
                                sm: 12,
                                md: 12,
                                lg: 12,
                                xl: 6,
                                xxl: 6,
                            }}
                            netFlowsByAssetClass={netFlowsByAssetClass}
                            netFlowsByAsset={netFlowsByAsset}
                            netFlowsByManager={netFlowsByManager}
                            netFlowAssetLabels={netFlowAssetLabels}
                        />
                        <LatestAssetsCard
                            assets={latestAssets.rows}
                            total={latestAssets.total}
                            colSpans={{
                                sm: 12,
                                md: 12,
                                lg: 12,
                                xl: 6,
                                xxl: 6,
                            }}
                        />
                    </VisualizationGrid>

                    <GlobalAssetTableLayout
                        initialState={globalAssetTable.initialState}
                        filterMetadata={globalAssetTable.filterMetadata}
                        stats={globalAssetTable.stats}
                        showFilters={true}
                    />
                </PageWrapper>
            </SidebarLayout>
        </PageTrack>
    );
}
